import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { Facebook, Youtube, Instagram, Phone, Mail, MapPin } from 'lucide-react';

const pageHref = (page) => {
    if (page.url) {
        return page.url.startsWith('/') ? page.url : '/' + page.url;
    }
    return `/content/${page.id}`;
};

const pickName = (page, lang) => {
    if (lang === 'ko' && page.nameKr) return page.nameKr;
    if (lang === 'en' && page.nameEn) return page.nameEn;
    return page.name || '';
};

const Footer = () => {
    const { t } = useTranslation();
    const footerMenus = useSelector((s) => s.system.footerMenus || []);
    const reduxLang = useSelector((s) => s.system.lang);
    const lang = i18n.language || reduxLang || 'mn';

    const col1 = footerMenus.filter((p) => (p.footerColumn || 1) === 1);
    const col2 = footerMenus.filter((p) => (p.footerColumn || 1) === 2);
    const col3 = footerMenus.filter((p) => (p.footerColumn || 1) === 3);

    const renderColumn = (items, title) => (
        <div>
            <h3 className="font-bold text-sm tracking-widest mb-6">{title}</h3>
            <ul className="space-y-3">
                {items.map((page) => (
                    <li key={page.id}>
                        <Link
                            href={pageHref(page)}
                            className="text-gray-300 hover:text-white transition"
                        >
                            {pickName(page, lang)}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );

    return (
        <footer className="bg-[#1a1a1a] text-white">
            <div className="max-w-7xl mx-auto px-6 py-16">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-16">
                    {/* Contact */}
                    <div>
                        <Link href="/" className="inline-block mb-6">
                            <Image
                                src="/logo.png"
                                alt="Хүрээ Тулга"
                                width={120}
                                height={120}
                                className="object-contain"
                            />
                        </Link>
                        <div className="space-y-3 text-sm text-gray-300">
                            <div className="flex items-start gap-3">
                                <MapPin size={18} className="mt-0.5 flex-shrink-0" />
                                <span>{t('footer.address')}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <Phone size={18} className="flex-shrink-0" />
                                <div className="flex flex-col">
                                    <a
                                        href="tel:+97699822874"
                                        className="hover:text-white transition"
                                    >
                                        +976 9982-2874
                                    </a>
                                    <a
                                        href="tel:+97680222214"
                                        className="hover:text-white transition"
                                    >
                                        +976 8022-2214
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Mail size={18} />
                                <a
                                    href="mailto:hureetulga@huree.edu.mn"
                                    className="hover:text-white transition"
                                >
                                    hureetulga@huree.edu.mn
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Dynamic footer columns */}
                    {footerMenus.length > 0 ? (
                        <>
                            {renderColumn(col1, t('footer.mainNav'))}
                            {renderColumn(col2, t('footer.information'))}
                            {renderColumn(col3, t('footer.quickLinks'))}
                        </>
                    ) : (
                        <>
                            <div>
                                <h3 className="font-bold text-sm tracking-widest mb-6">
                                    {t('footer.mainNav')}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    Хуудсуудыг админаас нэмнэ үү
                                </p>
                            </div>
                            <div />
                            <div />
                        </>
                    )}
                </div>

                {/* Social Media */}
                <div className="flex justify-center items-center gap-6 py-8 border-t border-gray-800">
                    <a
                        href="https://www.facebook.com/profile.php?id=100057631951646"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className="text-gray-400 hover:text-white transition"
                    >
                        <Facebook size={22} />
                    </a>
                    <a
                        href="#"
                        aria-label="YouTube"
                        className="text-gray-400 hover:text-white transition"
                    >
                        <Youtube size={22} />
                    </a>
                    <a
                        href="#"
                        aria-label="Instagram"
                        className="text-gray-400 hover:text-white transition"
                    >
                        <Instagram size={22} />
                    </a>
                </div>

                {/* Copyright */}
                <div className="flex items-center justify-center text-xs text-gray-500 pt-6 border-t border-gray-800">
                    <p>{t('footer.copyright')}</p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;